import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, MenuItem, TextField, Paper, alpha,
} from '@mui/material';
import { DataGridPremium, GridColDef, GridColumnGroupingModel, GridRenderCellParams, GridRenderEditCellParams, GridRowModel, GridToolbar, useGridApiContext, useGridApiRef, GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, GRID_AGGREGATION_FUNCTIONS, type GridAggregationFunction, type GridAggregationModel, type GridApi, type GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import { ViewToolbar } from '@/components/ViewToolbar';
import { useGridStatePersistence } from '@/lib/useGridStatePersistence';
import { useMutation, useQueries, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePlanHeader } from '@/hooks/finance/usePlanHeader';
import { useMyEntryHeaders } from '@/finance/hooks/useMyEntryHeaders';
import { usePlanView, usePlanViews } from '@/hooks/finance/usePlanView';
import { useDistributionProfiles, useDistributionProfile } from '@/hooks/finance/useDistributionProfile';
import { useFiscalPeriods } from '@/hooks/finance/useFiscalPeriod';
import { useDimensionValues } from '@/hooks/finance/useDimensionValue';
import { useDimensionDefinitions } from '@/hooks/finance/useDimensionDefinition';
import { useCustomers } from '@/hooks/sales/useCustomer';
import { useTradeTypes } from '@/hooks/core/useTradeType';
import { useSuppliers } from '@/hooks/procurement/useSupplier';
import { useOrgRoleFunctions } from '@/hooks/core/useOrgRoleFunction';
import { InventoryProductGroupDto } from '@/hooks/inventory/useInventoryProductGroup.generated';
import {
  buildEntryRows, composeSaveCells, resolveSavedRowId, applyRowTotals, TOTAL_FIELD_PREFIX, unionRowAxes,
  measureFormatOptions, isTopDownMeasure, resolveWeights, applyRowEdit, cellKey, changedPeriodIds,
  ACCOUNT_AXIS_ID, rowFilterJson, accountAxisKey, isServerResolved, supportsHierarchyChildren,
  EntryRow, RowItem, RowAxis, SavedCell, MeasureLike,
  isInternalRevenueMeasure, INTERNAL_PRICE_FIELD_PREFIX, INTERNAL_PRICE_PENDING_FIELD,
  internalPriceBelowPurchasePrice, rowInternalPrice, sumByUnit, formatUnitTotals, cleanFloat, UnitTotal,
} from './planEntryRows';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CircleIcon from '@mui/icons-material/Circle';
import Tooltip from '@mui/material/Tooltip';
import { numberValueFormatter, formatNumber, parseLocaleNumber } from '@/lib/locale';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useEnumValueOptions } from '@/lib/enumGridFilters';
import { apiClient, getErrorMessage } from '@/lib/apiClient';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { usePrincipalLabel } from '@/components/forms/PrincipalPicker';
import { PlanHeaderActionsMenu } from '@/finance/PlanHeaderActionsMenu';

/** One server-resolved row value: the backing node's id + code/name (mirrors Finance ResolveRowValueDto). */
type ResolvedRowValue = {
  id: string;
  code: string;
  name: string;
  // Effective unit of a product-group row (nearest ancestor wins, resolved server-side —
  // internal-pricelist spec §12.3); null/absent for other backings and unit-less groups.
  effectiveUnitOfMeasureId?: string | null;
  effectiveUnitOfMeasureCode?: string | null;
};

/** A stable key per filtered row dimension (its dimension, root and filter) — for the query and lookup. */
function rowFilterKey(dim: { dimensionDefinitionId: string; rootRef?: unknown; filterJson?: unknown }): string {
  return `${dim.dimensionDefinitionId}|${(dim.rootRef as string) ?? ''}|${rowFilterJson(dim.filterJson) ?? ''}`;
}

/**
 * Bespoke plan-entry grid (permission-gated). Two entry points:
 *   /finance/plan-entry            → a picker of Plan Headers
 *   /finance/plan-entry/:headerId  → the entry grid for one header
 *
 * The grid lays the header's PlanView out the way the spec describes for ColumnAxis=PERIOD:
 * periods are columns; under each (section × row-dimension value) the INPUT measures stack as
 * rows. Edited cells are batched into POST /api/finance/planline/save-cells, which derives one
 * PlanLine per (row value, period) cell.
 *
 * First slice — known follow-ups: load existing values back into the grid, the fill handle for
 * range copy, DERIVED measures shown read-only, and (later) showing only headers assigned to the
 * current owner / an "assign to" flow.
 */
export function PlanEntryGrid() {
  const { headerId } = useParams<{ headerId?: string }>();
  return headerId ? <PlanEntryEditor headerId={headerId} /> : <PlanEntrySelector />;
}

// ── Selector ────────────────────────────────────────────────────────────────

function PlanEntrySelector() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Assignment-filtered server-side (spec: Plan-Entry-Assignment-Access): only headers the user
  // may enter (AssignedTo/Owner match) or oversee (ViewSubtree/ViewAll) — never the full list.
  const { data: headers, isLoading } = useMyEntryHeaders();
  const { data: planViews } = usePlanViews();

  // Deep-link pre-filtering (same `?filter.<field>` contract as EntityListView): the setup hub's
  // per-version "open entry" link narrows the picker to that version's headers — id-exact (labels
  // are optional and can collide), with `filterLabel` as the chip text. Dismissible chip.
  const [searchParams, setSearchParams] = useSearchParams();
  const versionIdFilter = searchParams.get('filter.planVersionId');
  const versionFilter = searchParams.get('filter.planVersionLabel');
  const versionChipLabel = searchParams.get('filterLabel.planVersionId') ?? versionFilter;

  // Scenario name and version label come from the header DTO itself (PlanEntryHeaderDto). They
  // used to be joined client-side off the full PlanVersion list, which is the only reason this
  // page needed Finance.PlanVersion.Read — a permission a budget-contributor is not meant to hold,
  // since the plan structure is the controller's surface. The plan view (the entry template the
  // grid opens with) is still joined from the view list, which the contributor does read.
  const viewNameById = useMemo(
    () => new Map((planViews ?? []).map(v => [v.id, v.name])),
    [planViews],
  );
  const rows = useMemo(() => (headers ?? []).map(h => ({
    ...h,
    planScenarioName: h.planScenarioName ?? '—',
    planVersionDisplay: h.planVersionLabel ?? (h.planVersionNo != null ? `v${h.planVersionNo}` : '—'),
    planViewName: viewNameById.get(h.planViewId) ?? '—',
  })).filter(r =>
    (!versionIdFilter || r.planVersionId === versionIdFilter)
    && (!versionFilter
      || r.planVersionLabel === versionFilter
      || r.planVersionDisplay === versionFilter)), [headers, viewNameById, versionIdFilter, versionFilter]);

  // Humanize the enum codes the same way the Plan Headers grid does (CENTRAL_COST → "Central Cost",
  // DRAFT → "Draft"), via the shared runtime-localized enum options.
  const { options: typeOptions } = useEnumValueOptions('PlanHeaderType');
  const { options: statusOptions } = useEnumValueOptions('SubmissionStatus');
  const typeLabels = useMemo(() => new Map(typeOptions.map(o => [o.value, o.label])), [typeOptions]);
  const statusLabels = useMemo(() => new Map(statusOptions.map(o => [o.value, o.label])), [statusOptions]);

  // Fixed widths so the table doesn't span the whole page; valueGetter returns the humanized
  // label so filtering/sorting operate on what's shown.
  // groupable:false locks the layout — the scenario grouping from initialState stays, but the
  // column menu no longer offers "Group by" / "Stop grouping" so it can't be changed at runtime.
  const selectorColumns = useMemo<GridColDef[]>(() => [
    { field: 'planScenarioName', headerName: t('DisplayName:PlanVersion.planScenarioId', { defaultValue: 'Plan Scenario' }), width: 240, groupable: false },
    { field: 'planVersionDisplay', headerName: t('DisplayName:PlanHeader.planVersionId', { defaultValue: 'Plan Version' }), width: 220, groupable: false },
    { field: 'name', headerName: t('DisplayName:PlanHeader.name', { defaultValue: 'Name' }), width: 320, groupable: false },
    { field: 'ownerOrgUnitName', headerName: t('DisplayName:PlanHeader.ownerOrgUnitId', { defaultValue: 'Planning Org Unit' }), width: 200, groupable: false },
    {
      field: 'headerType',
      headerName: t('DisplayName:PlanHeader.headerType', { defaultValue: 'Type' }),
      width: 180,
      groupable: false,
      valueGetter: (_value, row) => typeLabels.get(row.headerType) ?? row.headerType,
    },
    { field: 'planViewName', headerName: t('DisplayName:PlanHeader.planViewId', { defaultValue: 'Plan View' }), width: 200, groupable: false },
    {
      field: 'status',
      headerName: t('DisplayName:PlanHeader.status', { defaultValue: 'Status' }),
      width: 180,
      groupable: false,
      valueGetter: (_value, row) => statusLabels.get(row.status) ?? row.status,
    },
  ], [t, typeLabels, statusLabels]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('Menu:Finance.PlanEntry', { defaultValue: 'Budget Entry' })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('Finance.PlanEntry.PickHeader', { defaultValue: 'Pick a plan header to enter values for.' })}
      </Typography>

      {(versionIdFilter || versionFilter) && (
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mb: 2 }}
          label={`${t('DisplayName:PlanHeader.planVersionId', { defaultValue: 'Plan Version' })}: ${versionChipLabel ?? ''}`}
          onDelete={() => setSearchParams({}, { replace: true })}
        />
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <DataGridPremium
          autoHeight
          disableRowSelectionOnClick
          disablePivoting
          rows={rows}
          columns={selectorColumns}
          groupingColDef={{ headerName: t('DisplayName:PlanVersion.planScenarioId', { defaultValue: 'Plan Scenario' }), width: 260 }}
          defaultGroupingExpansionDepth={-1}
          onRowClick={params => {
            // Group rows carry no header; only navigate from a leaf (a real PlanHeader).
            if (params.row?.planVersionId) navigate(`/finance/plan-entry/${params.id}`);
          }}
          initialState={{
            density: 'compact',
            rowGrouping: { model: ['planScenarioName'] },
            columns: { columnVisibilityModel: { planScenarioName: false } },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      )}
    </Box>
  );
}

// ── Editor ──────────────────────────────────────────────────────────────────

/**
 * Locale-aware edit cell for the measure cells. Uses a plain text input (so the user can type the
 * locale decimal separator — e.g. `,` in sv — regardless of the browser locale) and parses the
 * typed string to a JS number via the active format locale on every change. The cell value stored
 * in the grid is always a real number, so downstream derivation/totals keep working. Its own text
 * state is never re-synced from the parsed value, so the comma the user types never flips to a dot.
 *
 * Two keyboard sub-states (spreadsheet-style):
 *  - "enter": just arrowed in — value selected, arrows jump cell-to-cell, typing overwrites.
 *  - "caret": entered via F2 (always) or Enter (on an untouched cell) — caret sits in the value,
 *    so ←/→/Home/End move within the number instead of jumping cells. Enter after typing, or in
 *    caret mode, commits and moves on (MUI default).
 */
function MeasureEditCell(props: GridRenderEditCellParams) {
  const { id, field, value, hasFocus } = props;
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [caretMode, setCaretMode] = useState(false);
  const dirtyRef = useRef(false);
  const [text, setText] = useState<string>(() =>
    typeof value === 'number' && Number.isFinite(value)
      ? formatNumber(value, { useGrouping: false, maximumFractionDigits: 20 })
      : '',
  );

  useEffect(() => {
    if (hasFocus) {
      const el = inputRef.current;
      el?.focus();
      // Select the whole value so arrowing into a cell shows it highlighted and the first
      // keystroke overwrites it (spreadsheet "enter" behaviour).
      if (!caretMode) el?.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFocus]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    dirtyRef.current = true;
    setText(raw);
    const n = parseLocaleNumber(raw);
    // A blank/unparseable input commits as '' — the numeric column's valueParser maps '' to null
    // (MUI's cleared-cell convention), which the row model reads as a blank cell. Committing
    // `undefined` instead would be parsed as Number(undefined) = NaN, so blanking a cell would
    // leave NaN in the row instead of a proper blank.
    apiRef.current.setEditCellValue({ id, field, value: Number.isNaN(n) ? '' : n });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // F2 (always) or Enter (on an untouched cell) → enter caret edit mode: drop the selection and
    // place the caret at the end so ←/→ edit within the number.
    if (e.key === 'F2' || (e.key === 'Enter' && !caretMode && !dirtyRef.current)) {
      e.preventDefault();
      e.stopPropagation();
      setCaretMode(true);
      const el = inputRef.current;
      if (el) {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
      return;
    }
    // In caret mode, keep horizontal arrows / Home / End inside the input (don't let the grid's
    // onCellKeyDown jump to the next cell).
    if (caretMode && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End')) {
      e.stopPropagation();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      style={{
        width: '100%',
        height: '100%',
        textAlign: 'right',
        border: 'none',
        outline: 'none',
        padding: '0 10px',
        fontSize: 'inherit',
        fontFamily: 'inherit',
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
      }}
    />
  );
}

/**
 * Persists the entry grid's layout (column widths/visibility — incl. the months toggle —
 * sorting, density) to localStorage per plan view, via the house model
 * (useGridStatePersistence, same payload as the saved-views feature). Column ORDER and PINNING
 * are excluded: both are owned by the Plan View definition (measure sortOrder / pinnedLeftFields),
 * and a stored order predating a view change (e.g. a measure added) would otherwise re-apply and
 * push the new measure's columns to the far right, out of their month groups.
 * A separate component mounted right AFTER <DataGridPremium> in the same conditional JSX on
 * purpose: the editor has loading early-returns, so its own mount-time effects run while the
 * spinner is up — apiRef.current is still null and the hook would silently do nothing (same
 * timing trap as the cellFocusIn subscription). Mounted with the grid, its effects run after
 * the grid's, when the api exists.
 */
function GridLayoutPersistence({ apiRef, variant }: { apiRef: RefObject<GridApi | null>; variant: string }) {
  useGridStatePersistence(apiRef, 'Finance', 'PlanEntry', variant, { columnOrder: false, pinning: false });
  return null;
}

function PlanEntryEditor({ headerId }: { headerId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();
  // Drives number formatting; also a `columns` memo dependency so cells re-format live when the
  // user changes their format locale (the valueFormatter reads the active locale at call time).
  const { formatLocale } = useUserPreferences();

  // The column group whose header is highlighted — the one holding the focused cell: a period id
  // for a month, or the TOTAL_FIELD_PREFIX sentinel for the Totalt group. Focusing an ungrouped
  // cell (Internpris, row labels) clears it.
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

  const { data: header, isLoading: headerLoading } = usePlanHeader(headerId);
  // The caller's server-computed edit right for this header (spec: Plan-Entry-Assignment-Access):
  // false for subtree/ViewAll oversight viewers and for SUBMITTED headers. UX mirror only — the
  // save endpoint re-enforces it. Defaults to read-only until the list has loaded.
  const { data: myHeaders } = useMyEntryHeaders();
  const canEdit = myHeaders?.find(h => h.id === headerId)?.canEdit ?? false;
  const { data: planView, isLoading: viewLoading } = usePlanView(header?.planViewId ?? '', {
    enabled: !!header?.planViewId,
  });
  const { data: allPeriods } = useFiscalPeriods();
  const { data: allValues, error: allValuesError } = useDimensionValues();

  // The grid's period columns are the months of the scenario's fiscal year (the planning horizon).
  // The backend resolves it via a cheap projection (PlanHeader -> PlanVersion -> PlanScenario.FiscalYearId)
  // so we avoid loading the snapshot-heavy PlanVersion aggregate just to read one FK.
  const { data: fiscalYearId } = useQuery({
    queryKey: ['planHeaderFiscalYear', headerId],
    queryFn: async () =>
      (await apiClient.get<string | null>(`/api/finance/planheader/fiscal-year-id/${headerId}`)).data,
    enabled: !!headerId,
  });

  // Previously saved figures for this header (the read-side mirror of save-cells).
  const { data: savedCells } = useQuery({
    queryKey: ['planEntryCells', headerId],
    queryFn: async () =>
      (await apiClient.get<SavedCell[]>(`/api/finance/planline/cells/${headerId}`)).data,
  });

  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);

  // Humanized submission status for the read-only header-context strip (SUBMITTED → "Submitted"),
  // the same runtime-localized enum options the selector grid uses.
  const { options: statusOptions } = useEnumValueOptions('SubmissionStatus');
  const statusLabel = useMemo(
    () => statusOptions.find(o => o.value === header?.status)?.label ?? header?.status ?? '',
    [statusOptions, header?.status],
  );

  // Resolve the Assigned To principal (type + id) to its display name, so it shows as plain read-only
  // text like the other fields. Reuses the Plan Header form's resolver — no duplicated by-id lookup.
  const assignedTo = usePrincipalLabel({
    type: header?.assignedToPrincipalType,
    id: header?.assignedToPrincipalId,
    qualifier: header?.assignedToPrincipalQualifier,
  });
  // For an Org Role principal the resolver returns only the org unit, so prepend the role (function)
  // name — e.g. "Sales Representative — Sales Department (DEPT-SALES)" — resolving the qualifier code
  // to its OrgRoleFunction name. Other principal types (User/Role/Org Unit) show the base label as-is.
  const { data: roleFunctions } = useOrgRoleFunctions();
  const assignedToLabel = useMemo(() => {
    const base = assignedTo?.label ?? '';
    if (!base) return '';
    if (header?.assignedToPrincipalType === 'ORG_ROLE' && header?.assignedToPrincipalQualifier) {
      const fn = roleFunctions?.find(r => r.code === header.assignedToPrincipalQualifier);
      return `${fn?.name ?? header.assignedToPrincipalQualifier} — ${base}`;
    }
    return base;
  }, [assignedTo?.label, roleFunctions, header?.assignedToPrincipalType, header?.assignedToPrincipalQualifier]);

  // Planning Org Unit resolved from its id via the same by-id resolver (an OrgUnit is a valid principal
  // type). The denormalized header.orgUnitName isn't reliably populated on the detail DTO, so we
  // resolve rather than depend on it — mirrors how the Plan Header form shows this field.
  const ownerOrgUnit = usePrincipalLabel({ type: 'ORG_UNIT', id: header?.ownerOrgUnitId });

  // Periods that are columns: all months of the scenario's fiscal year (the planning horizon),
  // derived from the header's fiscal year — no per-header period range.
  const periods = useMemo(() => {
    if (!allPeriods || !fiscalYearId) return [];
    return allPeriods
      .filter(p => p.fiscalYearId === fiscalYearId)
      .sort((a, b) => a.periodNumber - b.periodNumber);
  }, [allPeriods, fiscalYearId]);

  // All row axes of the view, outermost first (group rows by the outer ones; the innermost is the leaf
  // data row). Two source kinds: DIMENSION axes (a DimensionDefinition's values) and ACCOUNT axes (GL
  // accounts entered directly — PlanView-Account-Rows Phase 2). The dimension-specific plumbing below
  // (hierarchy subtrees, customers, filtered dimension resolution) works on the DIMENSION subset, which
  // also narrows `dimensionDefinitionId` to a defined string.
  const allRowDims = useMemo(
    () => (planView?.rowDimensions ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [planView],
  );
  const rowDims = useMemo(
    () => allRowDims
      .filter((d): d is typeof d & { dimensionDefinitionId: string } =>
        d.rowSourceKind !== 'ACCOUNT' && d.dimensionDefinitionId != null),
    [allRowDims],
  );
  // Each row dim references a DimensionDefinition; its valueSource/backingEntity decide the row source
  // (free DimensionValues vs an entity-backed master list). Map definitionId → backingEntity.
  const { data: definitions, error: definitionsError } = useDimensionDefinitions();
  const backingByDefId = useMemo(() => {
    const m = new Map<string, string | undefined>();
    for (const d of definitions ?? []) m.set(d.id, d.backingEntity);
    return m;
  }, [definitions]);
  // A FREE dimension has no backingEntity, so its server-resolved (filtered) axis is keyed on the
  // valueSource instead — the rows are the dimension's own DimensionValue records.
  const valueSourceByDefId = useMemo(() => {
    const m = new Map<string, string | undefined>();
    for (const d of definitions ?? []) m.set(d.id, d.valueSource);
    return m;
  }, [definitions]);

  // Only a HIERARCHICAL backing has a tree to enumerate: a stored HIERARCHY_CHILDREN on a FLAT backing
  // (Kund, Affärsform, Leverantör) is stale config — RootRef is a node of the ProductGroup tree, so
  // honouring it there would list product groups as that axis's rows. Such an axis is treated as its
  // flat self instead and enumerates its own master list. While the dimension list is still loading the
  // backing is unknown, so the stored enumeration is taken at its word — that keeps the subtree fetch
  // starting immediately (no definitions → subtree waterfall); the classification settles on arrival.
  const isHierarchyAxis = useCallback(
    (d: { dimensionDefinitionId: string; rowEnumeration?: string | null }) =>
      d.rowEnumeration === 'HIERARCHY_CHILDREN'
      && (definitions == null || supportsHierarchyChildren(
        backingByDefId.get(d.dimensionDefinitionId), valueSourceByDefId.get(d.dimensionDefinitionId))),
    [definitions, backingByDefId, valueSourceByDefId],
  );

  // ACCOUNT axes always enumerate server-side (the account catalog is scoped to the header's LE and a
  // FILTERED axis pushes its predicate to SQL) — there is no client-side account master list to fall
  // back on, and there shouldn't be: the full chart of accounts never reaches the browser unfiltered.
  const accountDims = useMemo(
    () => allRowDims.filter(d => d.rowSourceKind === 'ACCOUNT'),
    [allRowDims],
  );

  // A HIERARCHY_CHILDREN axis lists the direct children of its root node, driven from the live tree
  // (DimensionValues don't exist until a cell is saved — lazy materialization). The cell-save engine still
  // carries one ProductGroupId, so a view has at most one PRODUCT-GROUP dimension — but that dimension may
  // appear several times with DIFFERENT roots (PlanView-RowAxis-Composition Phase 1), and their children
  // union into one axis. Fetch each distinct root's subtree and merge (deduped by node id).
  const hierarchyDims = useMemo(
    () => rowDims.filter(isHierarchyAxis),
    [rowDims, isHierarchyAxis],
  );
  const hierarchyRoots = useMemo(
    () => [...new Set(hierarchyDims.map(d => d.rootRef as string | undefined).filter((r): r is string => !!r))],
    [hierarchyDims],
  );
  // `combine` MUST be a stable reference — react-query re-runs it (and returns a new array) whenever the
  // function identity changes, so an inline combine would hand back a fresh `subtree` every render →
  // rowAxes → buildRows → the setRows effect would loop forever and the grid would render empty.
  const combineSubtrees = useCallback(
    (results: UseQueryResult<InventoryProductGroupDto[]>[]) => {
      const byId = new Map<string, InventoryProductGroupDto>();
      for (const r of results) for (const n of r.data ?? []) byId.set(n.id, n);
      // Failed fetches ride along so the grid can tell "no children" from "couldn't load" —
      // an errored subtree must never render as an empty axis (see axisLoadErrors).
      return {
        nodes: [...byId.values()],
        errors: results.filter(r => r.error).map(r => getErrorMessage(r.error)),
      };
    },
    [],
  );
  const subtree = useQueries({
    queries: hierarchyRoots.map(root => ({
      queryKey: ['productGroupSubtree', root],
      queryFn: async () =>
        (await apiClient.get<InventoryProductGroupDto[]>(
          `/api/inventory/inventory-product-groups/${root}/subtree`)).data,
    })),
    combine: combineSubtrees,
  });

  // A deactivated DimensionDefinition must not back a row axis: deactivating a definition does NOT
  // cascade to its DimensionValues, so those values stay active and would otherwise still enumerate as
  // rows — and a saved cell carrying a since-removed value id then fails on save. A dimension retired
  // from a view (e.g. swapped for another) drops out here. `false` only when definitions are loaded and
  // the flag is explicitly off, so nothing is dropped while the list is still loading.
  const activeByDefId = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const d of definitions ?? []) m.set(d.id, d.isActive);
    return m;
  }, [definitions]);

  // A SALES_CUSTOMER-backed row axis lists customers (flat, lazy-materialised) — fetch the master list
  // only when such an axis exists.
  const anyCustomerAxis = useMemo(
    () => rowDims.some(d => backingByDefId.get(d.dimensionDefinitionId) === 'SALES_CUSTOMER'),
    [rowDims, backingByDefId],
  );
  const { data: customers, error: customersError } = useCustomers({ enabled: anyCustomerAxis });

  // Likewise for a CORE_TRADE_TYPE-backed row axis: the flat trade-type catalog (no active flag — all
  // rows list), fetched only when such an axis exists.
  const anyTradeTypeAxis = useMemo(
    () => rowDims.some(d => backingByDefId.get(d.dimensionDefinitionId) === 'CORE_TRADE_TYPE'),
    [rowDims, backingByDefId],
  );
  const { data: tradeTypes, error: tradeTypesError } = useTradeTypes({ enabled: anyTradeTypeAxis });

  // Likewise for a PROCUREMENT_SUPPLIER-backed row axis: the flat supplier master, ACTIVE only
  // (BLOCKED/INACTIVE are excluded from budget rows, matching the server-side seam).
  const anySupplierAxis = useMemo(
    () => rowDims.some(d => backingByDefId.get(d.dimensionDefinitionId) === 'PROCUREMENT_SUPPLIER'),
    [rowDims, backingByDefId],
  );
  const { data: suppliers, error: suppliersError } = useSuppliers({ enabled: anySupplierAxis });

  // Row dimensions narrowed by a composable filter (PlanView-RowAxis-Composition Phase 2): resolve their
  // values server-side, so the predicate is pushed to SQL and only matching rows reach the grid (it
  // scales to large backing tables). All entity-backed dimensions route here (product-group children and
  // the flat customer/trade-type sets), and so do free dimensions (their own DimensionValue rows);
  // non-filtered axes still enumerate client-side.
  const filteredDims = useMemo(
    () => rowDims.filter(d => isServerResolved(
      d.filterJson,
      backingByDefId.get(d.dimensionDefinitionId),
      valueSourceByDefId.get(d.dimensionDefinitionId))),
    [rowDims, backingByDefId, valueSourceByDefId],
  );
  // Stable combine (same reasoning as combineSubtrees): zip results back to their dim by filter key.
  // Return a plain object, NOT a Map: react-query's structural sharing keeps a deep-equal plain object's
  // reference stable across refetches, whereas a fresh Map is a new reference every time — and a new
  // `resolvedByFilter` would ripple through rowAxes → buildRows and rebuild the grid needlessly.
  const combineResolved = useCallback(
    (results: UseQueryResult<ResolvedRowValue[]>[]) => {
      const byFilter: Record<string, ResolvedRowValue[]> = {};
      filteredDims.forEach((d, i) => {
        const data = results[i]?.data;
        if (data) byFilter[rowFilterKey(d)] = data;
      });
      return {
        byFilter,
        errors: results.filter(r => r.error).map(r => getErrorMessage(r.error)),
      };
    },
    [filteredDims],
  );
  const resolvedByFilter = useQueries({
    queries: filteredDims.map(d => ({
      queryKey: ['resolveRowValues', d.dimensionDefinitionId, (d.rootRef as string) ?? null, rowFilterJson(d.filterJson)],
      queryFn: async () =>
        (await apiClient.post<ResolvedRowValue[]>('/api/finance/planline/resolve-row-values', {
          dimensionDefinitionId: d.dimensionDefinitionId,
          rootRef: (d.rootRef as string) ?? null,
          filterJson: rowFilterJson(d.filterJson),
        })).data,
    })),
    combine: combineResolved,
  });

  // ACCOUNT axes: the active GL accounts of the header's LegalEntity, resolved server-side per axis
  // (its composable filter pushed to SQL), keyed by the axis's filter so several ACCOUNT fragments with
  // different filters each get their rows and then union into one axis. Same stable-combine reasoning
  // as combineResolved: a plain object keeps structural sharing across refetches.
  // accountAxisKey (imported, pure) keys an account fragment by its AccountGroup root + filter.
  const combineAccountRows = useCallback(
    (results: UseQueryResult<ResolvedRowValue[]>[]) => {
      const byKey: Record<string, ResolvedRowValue[]> = {};
      accountDims.forEach((d, i) => {
        const data = results[i]?.data;
        if (data) byKey[accountAxisKey(d)] = data;
      });
      return {
        byKey,
        errors: results.filter(r => r.error).map(r => getErrorMessage(r.error)),
      };
    },
    [accountDims],
  );
  const accountRowsByKey = useQueries({
    queries: accountDims.map(d => ({
      queryKey: ['resolveAccountRows', headerId, (d.accountGroupRootRef as string) ?? null, rowFilterJson(d.filterJson) ?? null],
      queryFn: async () =>
        (await apiClient.post<ResolvedRowValue[]>('/api/finance/planline/resolve-row-values', {
          rowSourceKind: 'ACCOUNT',
          planHeaderId: headerId,
          // RootRef is reused for the ACCOUNT case: the AccountGroup subtree root (HIERARCHY_CHILDREN);
          // null = the whole LE chart of accounts (ALL_ACTIVE_VALUES / FILTERED). See ResolveRowValuesDto.
          rootRef: (d.accountGroupRootRef as string) ?? null,
          filterJson: rowFilterJson(d.filterJson),
        })).data,
    })),
    combine: combineAccountRows,
  });

  // The value-set (RowItem[]) of each row axis, in row-axis order (outermost first), plus an
  // entity-backed read-back mapper (saved DimensionValue id → ValueCode → item key).
  // One axis-fragment per row dimension; same-source fragments are then unioned into a single axis
  // (their children/values merge — see unionRowAxes; all ACCOUNT fragments share ACCOUNT_AXIS_ID), so
  // the grid cartesian-products across distinct sources only (e.g. Account × Sortiment).
  const rowAxes = useMemo<RowAxis[]>(() => {
    // The DIMENSION-axis fragment (free values / hierarchy children / flat customers / filtered) —
    // unchanged in behaviour from the dimension-only grid.
    const dimensionAxis = (dim: (typeof rowDims)[number]): RowAxis => {
      const backing = backingByDefId.get(dim.dimensionDefinitionId);
      const valueSource = valueSourceByDefId.get(dim.dimensionDefinitionId);
      const isFree = valueSource === 'FREE';
      // HIERARCHY_CHILDREN only makes an axis hierarchical on a hierarchical backing (see isHierarchyAxis).
      const isHierarchy = isHierarchyAxis(dim);
      const isCustomer = backing === 'SALES_CUSTOMER';
      const isTradeType = backing === 'CORE_TRADE_TYPE';
      const isSupplier = backing === 'PROCUREMENT_SUPPLIER';
      // An axis narrowed by a composable filter is enumerated server-side (resolvedByFilter).
      const isFiltered = isServerResolved(dim.filterJson, backing, valueSource);
      let items: RowItem[];
      if (isFiltered) {
        // Server-narrowed values — same item shape as the matching unfiltered branch, so cell save/read-back
        // (carried by ProductGroupId/CustomerId/TradeTypeId, materialised + mapped by code — or, for a free
        // dimension, by the DimensionValue id itself) is unchanged.
        const resolved = resolvedByFilter.byFilter[rowFilterKey(dim)] ?? [];
        // BOTH product-group branches (this filtered one and the HIERARCHY_CHILDREN one below) must
        // carry the effective unit, or filtered views silently lose the row suffix (spec §12.3).
        items = isFree
          ? resolved.map(v => ({ key: v.id, label: v.name, rowValueId: v.id }))
          : isCustomer
            ? resolved.map(n => ({ key: n.id, label: n.name, customerId: n.id, code: n.code }))
            : isTradeType
              ? resolved.map(n => ({ key: n.id, label: n.name, tradeTypeId: n.id, code: n.code }))
              : isSupplier
                ? resolved.map(n => ({ key: n.id, label: n.name, supplierId: n.id, code: n.code }))
                : resolved.map(n => ({
                    key: n.id, label: n.name, productGroupId: n.id, code: n.code,
                    unitOfMeasureId: n.effectiveUnitOfMeasureId ?? undefined,
                    unitCode: n.effectiveUnitOfMeasureCode ?? undefined,
                  }));
      } else if (isHierarchy) {
        const root = dim.rootRef as string | undefined;
        // Direct children of the root only (one level down — no grandchildren), active.
        items = subtree.nodes
          .filter(n => n.parentId === root && n.isActive)
          .map(n => ({
            key: n.id, label: n.name, productGroupId: n.id, code: n.code,
            unitOfMeasureId: n.effectiveDefaultUnitOfMeasureId ?? undefined,
            unitCode: n.effectiveDefaultUnitOfMeasureCode ?? undefined,
          }));
      } else if (isCustomer) {
        // Flat entity-backed: all active customers (lazy materialisation — values come from the master list).
        items = (customers ?? [])
          .filter(c => c.isActive)
          .map(c => ({ key: c.id, label: c.name, customerId: c.id, code: c.customerNumber }));
      } else if (isTradeType) {
        // Flat entity-backed: all trade types (no active flag), in catalog order (sortOrder, then name).
        items = [...(tradeTypes ?? [])]
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
          .map(tt => ({ key: tt.id, label: tt.name, tradeTypeId: tt.id, code: tt.code }));
      } else if (isSupplier) {
        // Flat entity-backed: all ACTIVE suppliers (lazy materialisation — values come from the master list).
        items = (suppliers ?? [])
          .filter(s => s.status === 'ACTIVE')
          .map(s => ({ key: s.id, label: s.name, supplierId: s.id, code: s.supplierNumber }));
      } else {
        // ALL_ACTIVE_VALUES: the dimension's active values. (A FREE dimension's filter is not yet
        // server-resolved — only entity-backed axes route through resolvedByFilter above.)
        items = (allValues ?? [])
          .filter(v => v.dimensionDefinitionId === dim.dimensionDefinitionId && v.isActive)
          .map(v => ({ key: v.id, label: v.name, rowValueId: v.id }));
      }
      // Entity-backed axes (hierarchy, flat customer/trade type, or any filtered entity) need a saved-value
      // → item-key mapper: their item key is the BACKING record's id, so a saved DimensionValue id has to
      // travel through the value code to find its row. A free axis — filtered or not — keys its items on
      // the DimensionValue id itself, so the mapping is identity and no mapper is built.
      let valueIdToKey: ((valueId: string) => string | undefined) | undefined;
      if (!isFree && (isHierarchy || isCustomer || isTradeType || isSupplier || isFiltered)) {
        const valueCodeById = new Map<string, string>();
        for (const v of allValues ?? []) {
          if (v.dimensionDefinitionId === dim.dimensionDefinitionId) valueCodeById.set(v.id, v.valueCode);
        }
        const keyByCode = new Map<string, string>();
        for (const it of items) if (it.code) keyByCode.set(it.code, it.key);
        valueIdToKey = (valueId: string) => {
          const code = valueCodeById.get(valueId);
          return code ? keyByCode.get(code) : undefined;
        };
      }
      return {
        dimensionDefinitionId: dim.dimensionDefinitionId,
        name: dim.dimensionDefinitionName ?? '',
        // A filtered product-group axis is still hierarchical; a filtered customer axis stays flat.
        isHierarchy: isHierarchy || (isFiltered && backing === 'INVENTORY_PRODUCT_GROUP'),
        items,
        valueIdToKey,
      };
    };

    // The ACCOUNT-axis fragment: server-resolved accounts (id/number/name in BAS order). The account IS
    // the row key, so the saved-cell mapping is identity via SavedCell.accountId — no valueIdToKey.
    const accountAxis = (dim: (typeof accountDims)[number]): RowAxis => ({
      dimensionDefinitionId: ACCOUNT_AXIS_ID,
      name: t('Finance.PlanEntry.AccountAxis', { defaultValue: 'Account' }),
      isHierarchy: false,
      isAccount: true,
      items: (accountRowsByKey.byKey[accountAxisKey(dim)] ?? []).map(a => ({
        key: a.id,
        label: `${a.code} ${a.name}`,
        accountId: a.id,
        code: a.code,
      })),
    });

    // A DIMENSION axis without its dimension (invalid — the server-side XOR guard rejects it at
    // authoring) is skipped rather than allowed to blank the whole grid as an empty axis. A DIMENSION
    // axis whose definition has been deactivated is skipped too (a retired dimension no longer enumerates
    // rows — see activeByDefId); ACCOUNT axes have no definition and are always kept.
    return unionRowAxes(allRowDims
      .filter(dim => dim.rowSourceKind === 'ACCOUNT'
        || (dim.dimensionDefinitionId != null && activeByDefId.get(dim.dimensionDefinitionId) !== false))
      .map(dim =>
        dim.rowSourceKind === 'ACCOUNT'
          ? accountAxis(dim)
          : dimensionAxis(dim as (typeof rowDims)[number])));
  }, [allRowDims, subtree, customers, tradeTypes, suppliers, allValues, backingByDefId, valueSourceByDefId, isHierarchyAxis, activeByDefId, resolvedByFilter, accountRowsByKey, accountAxisKey, t]);

  // Map a saved cell (its per-dimension values + its account) to the grid row id (the ordered
  // combination of axis keys).
  const resolveRowId = useCallback(
    (cell: SavedCell) => resolveSavedRowId(rowAxes, cell),
    [rowAxes],
  );

  // Failed axis-source fetches, surfaced as ERRORS. An unreachable master list and a genuinely
  // empty axis are indistinguishable downstream (items = []), so a fetch failure must never fall
  // through to the "has no values to show" warning — that text sends the user hunting for missing
  // data when the actual problem is the request.
  const axisLoadErrors = useMemo<string[]>(() => {
    // Gate each source's error on whether the CURRENT view actually needs it: the route stays
    // mounted across header navigation, and react-query keeps exposing a cached error even after
    // `enabled` flips false — without the gate a Trade Types failure would keep alerting on a
    // header whose view has no such axis.
    const hookErrors: Array<[string, unknown]> = [
      [t('Finance.PlanEntry.SourceDimensions', { defaultValue: 'Dimensions' }), rowDims.length > 0 ? definitionsError : null],
      [t('Finance.PlanEntry.SourceDimensionValues', { defaultValue: 'Dimension values' }), rowDims.length > 0 ? allValuesError : null],
      [t('Finance.PlanEntry.SourceCustomers', { defaultValue: 'Customers' }), anyCustomerAxis ? customersError : null],
      [t('Finance.PlanEntry.SourceTradeTypes', { defaultValue: 'Trade Types' }), anyTradeTypeAxis ? tradeTypesError : null],
      [t('Finance.PlanEntry.SourceSuppliers', { defaultValue: 'Suppliers' }), anySupplierAxis ? suppliersError : null],
    ];
    // The useQueries combines already stringified their errors; hook errors stringify here.
    const failed: Array<[string, string]> = [
      ...hookErrors
        .filter(([, error]) => error != null)
        .map(([source, error]): [string, string] => [source, getErrorMessage(error)]),
      ...subtree.errors.map((message): [string, string] =>
        [t('Finance.PlanEntry.SourceProductGroups', { defaultValue: 'Product groups' }), message]),
      ...resolvedByFilter.errors.map((message): [string, string] =>
        [t('Finance.PlanEntry.SourceFilteredRows', { defaultValue: 'Filtered row values' }), message]),
      ...accountRowsByKey.errors.map((message): [string, string] =>
        [t('Finance.PlanEntry.SourceAccounts', { defaultValue: 'Accounts' }), message]),
    ];
    return failed.map(([source, message]) =>
      t('Finance.PlanEntry.RowSourceLoadFailed', {
        source,
        message,
        defaultValue: 'Rows for "{{source}}" could not be loaded — the grid may be incomplete; reload to retry. ({{message}})',
      }));
  }, [rowDims.length, anyCustomerAxis, anyTradeTypeAxis, anySupplierAxis,
      definitionsError, allValuesError, customersError, tradeTypesError, suppliersError,
      subtree.errors, resolvedByFilter.errors, accountRowsByKey.errors, t]);

  // Load errors surface in the app-standard top banner (same placement as every other view), not
  // as inline grid alerts. They are DERIVED from query state — not clearable by setState — so
  // Clear dismisses the current set by key; a different failure set re-surfaces the banner.
  const [dismissedLoadKey, setDismissedLoadKey] = useState<string | null>(null);
  const loadErrorKey = axisLoadErrors.join('\n');
  const loadBannerErrors = useMemo<ValidationError[]>(
    () => (axisLoadErrors.length > 0 && loadErrorKey !== dismissedLoadKey
      ? axisLoadErrors.map(message => ({
          field: 'form',
          message,
          instancePath: '#/properties/form',
          members: ['form'],
        }))
      : []),
    [axisLoadErrors, loadErrorKey, dismissedLoadKey],
  );

  // R1 / config warnings, surfaced at render (never a silent empty grid or a silent collision).
  const configWarnings = useMemo<string[]>(() => {
    const w: string[] = [];
    // A view may use the SAME product-group dimension several times (different roots, unioned), but not
    // two DIFFERENT product-group dimensions — the cell-save engine carries one product group per cell.
    if (rowAxes.filter(a => a.isHierarchy).length > 1) {
      w.push(t('Finance.PlanEntry.MultipleHierarchyDims', {
        defaultValue: 'Only one product-group (Hierarchy Children) dimension is supported per view (a cell carries one product group). The same dimension may be added several times with different root nodes, but not two different product-group dimensions.',
      }));
    }
    if (hierarchyDims.some(d => !d.rootRef)) {
      w.push(t('Finance.PlanEntry.MissingRootRef', {
        defaultValue: 'A Hierarchy Children row dimension has no root node configured, so no rows can be shown. Set a root node on the row dimension.',
      }));
    }
    const hierAxis = rowAxes.find(a => a.isHierarchy);
    if (hierAxis && hierAxis.items.length > 0) {
      const codes = hierAxis.items.map(i => i.code).filter(Boolean) as string[];
      const dupes = [...new Set(codes.filter((c, i) => codes.indexOf(c) !== i))];
      if (dupes.length > 0) {
        w.push(t('Finance.PlanEntry.DuplicateRowCodes', {
          codes: dupes.join(', '),
          defaultValue: `Two or more row nodes share a code (${dupes.join(', ')}); those rows would collide on save (R1). Give each node a distinct code.`,
        }));
      }
    }
    // While an axis source failed to load, every axis's emptiness is unknowable — the load error
    // (rendered separately, severity error) is the truthful message, so the empty-axis warnings
    // stand down rather than mislead.
    if (axisLoadErrors.length === 0) {
      for (const axis of rowAxes) {
        if (axis.items.length === 0 && !(axis.isHierarchy && hierarchyDims.some(d => !d.rootRef))) {
          w.push(axis.isAccount
            ? t('Finance.PlanEntry.EmptyAccountAxis', {
                defaultValue: 'No active accounts match this view’s Account row axis (check the axis filter and the chart of accounts of the header’s legal entity).',
              })
            : t('Finance.PlanEntry.EmptyRowDimension', {
                name: axis.name,
                defaultValue: `Row dimension "${axis.name}" has no values to show. (An entity-backed dimension with no materialized values is not yet supported as a grouping axis.)`,
              }));
        }
      }
    }
    return w;
  }, [hierarchyDims, rowAxes, axisLoadErrors, t]);

  // All measures shown under each month, in order: INPUT ones are editable, DERIVED ones (e.g.
  // Värde = Volym × Pris) are read-only and computed live as the inputs change.
  const measures = useMemo(
    () => (planView?.measures ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [planView],
  );
  const inputMeasures = useMemo(() => measures.filter(m => m.kind === 'INPUT'), [measures]);
  // DERIVED measures recomputed client-side — the INTERNAL_REVENUE_LINE measure is DERIVED but
  // server-sourced (the engine resolves the internal price at save), so it is excluded here and
  // rendered as its own single read-only column (internal-pricelist spec §6.2).
  const derivedMeasures = useMemo(
    () => measures.filter(m => m.kind === 'DERIVED' && !isInternalRevenueMeasure(m)),
    [measures],
  );
  const internalPriceMeasures = useMemo(() => measures.filter(isInternalRevenueMeasure), [measures]);
  const periodMeasures = useMemo(() => measures.filter(m => !isInternalRevenueMeasure(m)), [measures]);
  // Humanized MappingReason for the Internpris PENDING tooltip (runtime-localized enum options).
  const { options: mappingReasonOptions } = useEnumValueOptions('MappingReason');
  // Read-only Total column per measure (sum across periods) at the left of the grid, opt-in per view.
  const showTotals = !!planView?.showTotals;
  // Top-down entry: type a total → spread it down to the months by a DistributionProfile. Only for
  // additive INPUT measures (QUANTITY/AMOUNT), only with period columns, and only when Show Totals is on.
  //
  // Mixed-unit scopes (internal-pricelist spec §12.3): every distribution scope reachable HERE is
  // single-unit by construction — the spread/broadcast runs across the PERIODS of one row (one
  // product group → one resolved unit), columnAxis is neutralized to PERIOD, and the cross-row
  // aggregate rows below are non-editable and unreachable by the keyboard lanes. So no mixed-unit
  // block is needed in the entry grid; §12.3's "rejected on mixed-unit scopes" bites when
  // consolidation's per-unit distribute-down lands (spec §12.4 — the SYS-18 rule names it).
  const allowTotalEntry = showTotals && planView?.columnAxis === 'PERIOD' && !!planView?.allowTotalEntry;
  // Every INPUT measure supports top-down entry into its Total cell: additive ones (Volym) are spread
  // across the months, rate/ratio ones (Pris) are broadcast (same value each month). DERIVED stays read-only.
  const topDownMeasures = useMemo(
    () => (allowTotalEntry ? measures.filter(isTopDownMeasure) : []),
    [allowTotalEntry, measures],
  );

  // Unit uniformity across the WHOLE view (user decision 2026-08-26): with at most one distinct
  // unit among the product-group row items — or no group axis at all — a plain quantity sum is
  // honest, so the column menu may offer ad-hoc aggregation on quantity columns too. Any mix
  // locks them to the fixed per-unit aggregation ("5 ha + 40 tim" is not a number).
  const { viewIsUnitUniform, viewUnitCode } = useMemo(() => {
    const groupItems = rowAxes.flatMap(a => a.items).filter(i => i.productGroupId);
    const unitKeys = new Set(groupItems.map(i => i.unitOfMeasureId ?? null));
    return {
      viewIsUnitUniform: unitKeys.size <= 1,
      viewUnitCode: unitKeys.size === 1 ? groupItems.find(i => i.unitCode)?.unitCode : undefined,
    };
  }, [rowAxes]);

  // Cross-row aggregation is MENU-OPT-IN ONLY (user decision 2026-08-26): the grid shows NO
  // aggregate rows by default — exactly the pre-Phase-4 behavior — and the user activates
  // aggregation per column through MUI's column menu. The registry holds the functions the menus
  // offer: MUI's built-ins (avg/min/max), our nexoSum (the built-in sum + blank-coercion and
  // float-cleaning; labeled 'sum' so menu and header chip read as the standard function), and —
  // on a mixed-unit view — one per-unit sum per QUANTITY field (spec §12.3: "5 ha + 40 tim" is
  // not a number, so the honest per-unit aggregate is the only sum offered there; it renders an
  // asterisk + per-unit tooltip). Pure client-side compute over the loaded rows — no network.
  // The per-field closures exist because getCellValue only receives the row.
  const aggregationFunctions = useMemo(() => {
    const functions: Record<string, GridAggregationFunction> = { ...GRID_AGGREGATION_FUNCTIONS };
    functions.nexoSum = {
      label: 'sum',
      columnTypes: ['number'],
      apply: ({ values }) => {
        let sum = 0;
        for (const v of values) {
          if (v === undefined || v === null || v === '') continue;
          const n = Number(v);
          if (!Number.isNaN(n)) sum += n;
        }
        return cleanFloat(sum);
      },
    };
    if (!viewIsUnitUniform) {
      for (const m of periodMeasures) {
        if (m.valueType !== 'QUANTITY') continue;
        const fields = [
          ...periods.map(p => `${p.id}__${m.code}`),
          `${TOTAL_FIELD_PREFIX}${m.code}`,
        ];
        for (const field of fields) {
          functions[`unitSum__${field}`] = {
            label: 'sum',
            columnTypes: ['number'],
            getCellValue: ({ row }) => ({
              value: (row as EntryRow)[field],
              unitId: (row as EntryRow).unitOfMeasureId,
              unitCode: (row as EntryRow).unitCode,
            }),
            apply: ({ values }) =>
              sumByUnit(values as Array<{ value: unknown; unitId?: string; unitCode?: string }>),
          };
        }
      }
    }
    return functions;
  }, [periodMeasures, periods, viewIsUnitUniform]);

  // The model is state so column-menu picks actually stick (a controlled model without a change
  // handler silently reverts). Starts — and stays — EMPTY until the user activates aggregation;
  // reset on a header switch or when the loaded axes flip the view's unit uniformity (a quantity
  // pick made under the wrong uniformity must not survive the flip).
  const [aggregationModel, setAggregationModel] = useState<GridAggregationModel>({});
  useEffect(() => { setAggregationModel({}); }, [headerId, viewIsUnitUniform]);

  // The spread curve: a reusable DistributionProfile (relative weights), defaulting to the view's
  // configured profile (null ⇒ even split). Ephemeral UI state — the chosen profile isn't persisted.
  const { data: profiles } = useDistributionProfiles({ enabled: allowTotalEntry });
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  useEffect(() => {
    setSelectedProfileId(planView?.defaultDistributionProfileId ?? '');
  }, [planView?.defaultDistributionProfileId]);
  const { data: selectedProfile } = useDistributionProfile(selectedProfileId, {
    enabled: allowTotalEntry && !!selectedProfileId,
  });
  const profileLines = selectedProfileId ? selectedProfile?.lines : undefined;

  const [rows, setRows] = useState<EntryRow[]>([]);

  // Track which column group holds the focused cell (click AND keyboard navigation both raise
  // cellFocusIn). Month fields are `${periodId}__${code}`, Total fields `__TOTAL__${code}`;
  // anything else clears the highlight.
  // apiRef.current is NULL until <DataGridPremium> has mounted, and the grid only renders once the
  // rows are built — so the subscription must re-attempt when `rows` lands (an effect keyed only on
  // mount runs while the loading spinner is up, silently subscribes to nothing, and never retries).
  useEffect(() => {
    const api = apiRef.current;
    if (!api || rows.length === 0) return;
    return api.subscribeEvent('cellFocusIn', (params) => {
      const field = String(params.field);
      if (field.startsWith(TOTAL_FIELD_PREFIX)) {
        setActiveGroupId(TOTAL_FIELD_PREFIX);
        return;
      }
      const prefix = field.split('__')[0];
      setActiveGroupId(periods.some(p => p.id === prefix) ? prefix : null);
    });
  }, [apiRef, periods, rows]);

  const [dirty, setDirty] = useState(false);
  // Which individual (row, period) cells changed since load, keyed via `cellKey`. Save posts ONLY
  // these — an untouched cell that was read from the server is never re-sent (each cell triggers the
  // full derivation pipeline server-side, so the payload size drives the save cost). Reset on
  // save/cancel/rebuild, alongside `dirty`.
  const [dirtyCells, setDirtyCells] = useState<ReadonlySet<string>>(() => new Set());
  // Mirror `dirty` into a ref so the rebuild effect below can read the latest value WITHOUT taking
  // `dirty` as a dependency — depending on it would rebuild the grid from the last-saved snapshot the
  // moment `dirty` flips back to false on Save, reverting to stale server data. This effect runs on
  // every commit where `dirty` changed, so the ref is current before the rebuild effect reads it.
  const dirtyRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  const buildRows = useCallback((): EntryRow[] => {
    // Cardinality guard: one cell carries one ProductGroupId, so two DIFFERENT product-group axes can't be
    // saved. The SAME dimension across several roots is unioned into ONE hierarchy axis (allowed), so count
    // distinct hierarchy axes (post-union), not the raw HIERARCHY_CHILDREN row count.
    if (rowAxes.filter(a => a.isHierarchy).length > 1) return [];
    const built = buildEntryRows(rowAxes, periods, measures, savedCells, resolveRowId);
    if (showTotals) for (const r of built) applyRowTotals(r, measures, periods);
    return built;
  }, [rowAxes, periods, measures, savedCells, resolveRowId, showTotals]);

  // (Re)build the grid from the saved server snapshot when the STRUCTURE changes (row axes, periods,
  // measures, saved cells). Guard against react-query reference churn clobbering in-progress edits:
  // `buildRows`'s identity also changes on a background refetch (staleTime is 30s) or when the
  // resolve-row-values `combine` hands back a fresh Map (Maps aren't structurally shared), and
  // rebuilding then would discard the values the user just typed — they live only in `rows` until Save.
  // While the grid is dirty, leave the working rows untouched.
  useEffect(() => {
    if (dirtyRef.current) return;
    const built = buildRows();
    setRows(built);
    setDirty(false);
    setDirtyCells(new Set());

    // Live Internpris preview (internal-pricelist spec §6.2): fill every row with the price that
    // WOULD apply, resolved server-side by the same engine the save uses. A saved cell's frozen
    // value (or PENDING flag) always wins over the preview; a no-match row stays blank — the save
    // is what surfaces problems authoritatively. Display-only; never part of the save payload.
    if (internalPriceMeasures.length === 0 || built.length === 0 || periods.length === 0) return;
    const targetDate = periods[0]?.startDate;
    if (!targetDate) return;
    let cancelled = false;
    apiClient
      .post<{ price?: number | null }[]>('/api/finance/planline/preview-internal-prices', {
        planHeaderId: headerId,
        targetDate,
        rows: built.map(r => ({
          tradeTypeId: r.tradeTypeId,
          productGroupId: r.productGroupId,
          supplierId: r.supplierId,
        })),
      })
      .then(({ data: previews }) => {
        if (cancelled || dirtyRef.current || previews.length !== built.length) return;
        setRows(prev => prev.map(row => {
          const idx = built.findIndex(b => b.id === row.id);
          const price = idx >= 0 ? previews[idx]?.price : undefined;
          if (price === undefined || price === null) return row;
          const patched = { ...row };
          for (const m of internalPriceMeasures) {
            const field = `${INTERNAL_PRICE_FIELD_PREFIX}${m.code}`;
            if (patched[field] === undefined && patched[INTERNAL_PRICE_PENDING_FIELD] === undefined) {
              patched[field] = price;
            }
          }
          return patched;
        }));
      })
      .catch(() => { /* preview is best-effort — the column simply stays blank */ });
    return () => { cancelled = true; };
  }, [buildRows, internalPriceMeasures, periods, headerId]);

  // The outer row dimensions (all but the innermost) group the rows; the innermost is the leaf row.
  const outerCount = Math.max(0, rowAxes.length - 1);
  const groupingFields = useMemo(() => Array.from({ length: outerCount }, (_, k) => `g${k}`), [outerCount]);
  const groupColumnVisibility = useMemo(
    () => Object.fromEntries(groupingFields.map(f => [f, false])) as Record<string, boolean>,
    [groupingFields],
  );

  // Controlled column visibility: the base is the always-hidden grouping source columns, on top of
  // which the user's column-panel choices, the saved layout (restored by GridLayoutPersistence) and
  // the months toggle below all land in one model.
  const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>(() => ({ ...groupColumnVisibility }));
  useEffect(() => {
    setVisibilityModel(prev => ({ ...prev, ...groupColumnVisibility }));
  }, [groupColumnVisibility]);

  // "Dölj/Visa månader": one click hides/shows every month column, leaving Internpris + the Totalt
  // section as a compact overview. Derived from the model (not separate state), so a manual
  // column-panel change and a restored saved layout both keep the button label truthful.
  const monthFields = useMemo(
    () => periods.flatMap(p => periodMeasures.map(m => `${p.id}__${m.code}`)),
    [periods, periodMeasures],
  );
  const monthsHidden = useMemo(
    () => monthFields.length > 0 && monthFields.every(f => visibilityModel[f] === false),
    [monthFields, visibilityModel],
  );
  const toggleMonths = useCallback(() => {
    setVisibilityModel(prev => {
      const hide = !(monthFields.length > 0 && monthFields.every(f => prev[f] === false));
      const next = { ...prev };
      for (const f of monthFields) {
        if (hide) next[f] = false;
        else delete next[f];
      }
      return next;
    });
  }, [monthFields]);

  // Pin the left section — the grouping/leaf row columns through the Totalt measure group — to the left,
  // so they stay visible while the month columns scroll horizontally.
  const pinnedLeftFields = useMemo(() => {
    const left: string[] = [];
    if (groupingFields.length > 0) left.push(GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD);
    left.push('rowLabel');
    // The single Internpris column lives in the pinned block regardless of ShowTotals (spec §6.2),
    // BEFORE the Totalt group — it is a rate input to the totals, not one of them.
    for (const m of internalPriceMeasures) left.push(`${INTERNAL_PRICE_FIELD_PREFIX}${m.code}`);
    for (const m of periodMeasures) left.push(`${TOTAL_FIELD_PREFIX}${m.code}`);
    return left;
  }, [groupingFields, periodMeasures, internalPriceMeasures]);

  // One column per (period, measure); the measures sit under a per-month column group below.
  const columns = useMemo<GridColDef[]>(() => {
    const cols: GridColDef[] = [];
    const allMeasures = [...periodMeasures, ...internalPriceMeasures];
    // OQ-1 advisory (spec §1.2): the warning sits on the PRICE the budgeter enters — that is the
    // value they can influence — never on the server-resolved Internpris. Renders the warning icon
    // next to an offending price value; live while typing and on read-back of saved rows.
    const priceWarningCell = (offending: { internalPrice: number; purchasePrice: number } | null, m: MeasureLike, formatted: unknown) => {
      if (!offending) return <>{(formatted as string) ?? ''}</>;
      const fmt = new Intl.NumberFormat(undefined, measureFormatOptions(m));
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, height: '100%', width: '100%' }}>
          <Tooltip
            title={t('Finance.PlanEntry.InternalPriceBelowPurchase', {
              defaultValue: 'Entered price ({{purchase}}) exceeds the internal price ({{internal}}) — the internal price must never be below the entered price.',
              internal: fmt.format(offending.internalPrice),
              purchase: fmt.format(offending.purchasePrice),
            })}
          >
            <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />
          </Tooltip>
          <span>{(formatted as string) ?? ''}</span>
        </Box>
      );
    };
    // Aggregated QUANTITY cells hold a per-unit breakdown (UnitTotal[]) instead of a number
    // (spec §12.3) — route arrays through formatUnitTotals; plain cells keep the numeric
    // formatter. Non-quantity measures format exactly as before. The formatter's inline
    // breakdown string survives for CLIPBOARD/EXPORT — on screen the render cell below shows
    // a plain number (uniform scope) or an asterisk (mixed), with the breakdown in a tooltip.
    const quantityAwareFormatter = (m: MeasureLike, opts: Intl.NumberFormatOptions) => {
      const base = numberValueFormatter(opts);
      if (m.valueType !== 'QUANTITY') return base;
      return (value: unknown): string =>
        Array.isArray(value)
          ? formatUnitTotals(value as UnitTotal[], v => formatNumber(v, opts))
          : base(value);
    };
    // SCREEN format vs CLIPBOARD format — they are not the same string, and which one a column's
    // `valueFormatter` produces is not a free choice: MUI serialises `formattedValue` into the
    // clipboard (csvSerializer.serializeCellValue), so the valueFormatter IS the clipboard format.
    // It therefore carries the machine-readable number — locale decimal separator, no thousands
    // grouping — because Intl's sv-SE group separator is U+00A0 (a non-breaking space) and Excel
    // reads a token containing one as TEXT, not as a number. The grouped, human-readable number is
    // what renderCell paints, so the grid looks exactly as before.
    // The clipboard also carries the FULL stored value, not the rounded one on screen: a measure
    // with displayDecimals 0 shows 1234,56 as "1 235", and copying that out (or back into another
    // cell) would silently rewrite the value. Excel behaves the same way — the cell shows the
    // format, the clipboard carries the number.
    const displayFormatter = (m: MeasureLike) => quantityAwareFormatter(m, measureFormatOptions(m));
    const clipboardFormatter = (m: MeasureLike) =>
      quantityAwareFormatter(m, { useGrouping: false, maximumFractionDigits: 20 });
    // A pasted cell arrives as raw clipboard TEXT and is parsed by the column, NOT by the cell
    // editor — so the editor's locale-aware reader never saw it. MUI's default numeric parser is
    // `Number(value)`, which is NaN for every grouped number ("2 946" out of Excel, or out of this
    // grid's own Ctrl+C), and the cell was silently BLANKED. parseLocaleNumber is the same reader
    // the editor uses: it strips grouping (NBSP included) and takes the locale decimal separator.
    const pastedNumber = (value: unknown) => {
      const text = value == null ? '' : String(value).trim();
      // An empty pasted cell clears the target, which is MUI's own numeric convention ('' → null).
      if (text === '') return null;
      const n = parseLocaleNumber(text);
      // Unparseable text leaves the cell untouched — `undefined` makes MUI skip it — rather than
      // writing NaN or wiping a value the user never meant to overwrite.
      return Number.isNaN(n) ? undefined : n;
    };
    // The three props every measure column shares: the clipboard-shaped formatter, the grouped
    // on-screen rendering, and the locale-aware paste parser. Columns with their own renderCell
    // (price warning, quantity aggregate, pending internal price) spread this first and override.
    const measureCellProps = (m: MeasureLike) => {
      const display = displayFormatter(m);
      return {
        valueFormatter: clipboardFormatter(m),
        renderCell: (params: GridRenderCellParams) => <>{display(params.value)}</>,
        pastedValueParser: pastedNumber,
      };
    };
    // Aggregated QUANTITY cell display (user decision 2026-08-26 — the inline "69 085 m3f · 8 6…"
    // breakdown truncated and read poorly): a single-unit scope renders the plain number, a
    // mixed-unit scope renders an asterisk, and BOTH carry the per-unit totals in a hover tooltip
    // (one unit per line; a unit-less bucket renders bare). Never a sum across units. Leaf cells
    // render exactly as before. On a uniform view the aggregate value is a plain number (nexoSum)
    // — the tooltip then names the view's single unit, since the cell itself is unsuffixed.
    const quantityAggregateCell = (m: MeasureLike) => (params: GridRenderCellParams) => {
      const value = params.value;
      const fmt = (n: number) => formatNumber(n, measureFormatOptions(m));
      // The GROUPED number, not params.formattedValue — that one is now the ungrouped clipboard
      // form (see displayFormatter/clipboardFormatter above).
      const display = displayFormatter(m);
      if (!Array.isArray(value)) {
        const isAggregateRow = params.rowNode != null && params.rowNode.type !== 'leaf';
        if (isAggregateRow && viewUnitCode && typeof value === 'number' && Number.isFinite(value)) {
          return (
            <Tooltip title={`${fmt(value)} ${viewUnitCode}`}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: '100%' }}>
                {display(value)}
              </Box>
            </Tooltip>
          );
        }
        return <>{display(value)}</>;
      }
      const totals = value as UnitTotal[];
      if (totals.length === 0) return <></>;
      const lines = totals.map(t => (t.unitCode ? `${fmt(t.sum)} ${t.unitCode}` : fmt(t.sum)));
      return (
        <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</Box>}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: '100%' }}>
            {totals.length > 1 ? '*' : fmt(totals[0].sum)}
          </Box>
        </Tooltip>
      );
    };
    // Aggregation via the column menu — the ONLY way to aggregate (user decision 2026-08-26; no
    // defaults). Offered per column: AMOUNT (Värde) sum/avg/min/max on every view (kr is
    // unit-free); RATE/RATIO (Pris) avg/min/max on every view (a sum of unit prices is not a
    // number anyone wants; a mean/extreme is the user's explicit choice); QUANTITY sum/avg/min/max
    // when the whole view is single-unit, but on a MIXED-unit view only the per-unit sum — the
    // honest aggregate (asterisk + per-unit tooltip; avg/min/max across ha and tim are as
    // undefined as the sum). "sum" everywhere is our nexoSum/unitSum, never MUI's built-in.
    const SUMMABLE_AGGREGATIONS = ['nexoSum', 'avg', 'min', 'max'];
    const RATE_AGGREGATIONS = ['avg', 'min', 'max'];
    const adHocAggregation = (m: MeasureLike, field: string) => {
      if (m.valueType === 'AMOUNT' || (m.valueType === 'QUANTITY' && viewIsUnitUniform)) {
        return { aggregable: true, availableAggregationFunctions: SUMMABLE_AGGREGATIONS };
      }
      if (m.valueType === 'QUANTITY') {
        return { aggregable: true, availableAggregationFunctions: [`unitSum__${field}`] };
      }
      if (m.valueType === 'RATE' || m.valueType === 'RATIO') {
        return { aggregable: true, availableAggregationFunctions: RATE_AGGREGATIONS };
      }
      return {};
    };
    // Hidden source columns for the outer row dimensions (their values surface in the
    // collapsible grouping column instead; the grouping itself is driven by rowGroupingModel,
    // which works regardless of the groupable flag).
    for (let k = 0; k < outerCount; k++) {
      cols.push({
        field: `g${k}`,
        headerName: rowAxes[k]?.name || t('Finance.PlanEntry.GroupN', { defaultValue: 'Group {{n}}', n: k + 1 }),
        width: 160,
      });
    }
    cols.push({
      field: 'rowLabel',
      headerName: rowAxes[rowAxes.length - 1]?.name || t('Finance.PlanEntry.Row', { defaultValue: 'Row' }),
      width: 180,
    });
    // The Internpris column (internal-pricelist spec §6.2): one read-only column per
    // INTERNAL_REVENUE_LINE measure — server-sourced, shown once (a rate that does not vary by
    // period in v1), pinned regardless of ShowTotals and placed BEFORE the Totalt group (it is a
    // rate input to the totals, not one of them). A PENDING row shows a warning icon with the
    // humanized MappingReason instead of a silently blank cell.
    for (const m of internalPriceMeasures) {
      cols.push({
        field: `${INTERNAL_PRICE_FIELD_PREFIX}${m.code}`,
        headerName: m.label,
        type: 'number',
        editable: false,
        width: 100,
        ...measureCellProps(m),
        // Internpris is a rate like Pris — same ad-hoc menu (avg/min/max, never a sum).
        ...adHocAggregation(m, `${INTERNAL_PRICE_FIELD_PREFIX}${m.code}`),
        cellClassName: 'nexo-readonly-cell',
        headerClassName: 'nexo-total-cell',
        renderCell: (params) => {
          const pendingReason = (params.row as EntryRow)[INTERNAL_PRICE_PENDING_FIELD] as string | undefined;
          if (pendingReason) {
            const reasonLabel =
              mappingReasonOptions.find(o => o.value === pendingReason)?.label ?? pendingReason;
            return (
              <Tooltip title={reasonLabel}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', color: 'warning.main' }}>
                  <WarningAmberIcon fontSize="small" />
                </Box>
              </Tooltip>
            );
          }
          return <>{displayFormatter(m)(params.value)}</>;
        },
      });
    }
    // Total cell per measure, left of the months (sum for additive measures, blank for rates). When the
    // view allows top-down entry, the Total of an additive INPUT measure is editable — typing it spreads
    // the value down to the months by the selected DistributionProfile.
    if (showTotals) {
      const topDownCodes = new Set(topDownMeasures.map(m => m.code));
      periodMeasures.forEach((m, idx) => {
        const totalEditable = topDownCodes.has(m.code);
        // The first/last Total columns carry the section's LEFT/RIGHT frame edges, so Totalt
        // reads as its own block between Internpris and the months. (MUI v8 dropped the old
        // `MuiDataGrid--pinnedColumns-left` wrapper, so the edge must live on the columns.)
        const first = idx === 0 ? ' nexo-total-first' : '';
        const last = idx === periodMeasures.length - 1 ? ' nexo-total-last' : '';
        cols.push({
          field: `${TOTAL_FIELD_PREFIX}${m.code}`,
          headerName: m.label,
          type: 'number',
          editable: totalEditable,
          // Amounts need room: the Total of a money measure carries a full-year sum
          // (millions incl. decimals) that truncates at the 90px measure default.
          width: m.valueType === 'AMOUNT' ? 130 : 90,
          ...measureCellProps(m),
          ...adHocAggregation(m, `${TOTAL_FIELD_PREFIX}${m.code}`),
          ...(totalEditable ? { renderEditCell: (params) => <MeasureEditCell {...params} /> } : {}),
          // The Totalt Pris cell carries the OQ-1 warning when ANY month's entered price exceeds
          // the internal price — visible even with the months hidden. Quantity totals get the
          // per-unit tooltip cell (plain number / asterisk) on the aggregate rows.
          ...(m.storageBinding === 'PLANLINE_UNITPRICE'
            ? {
                renderCell: (params: GridRenderCellParams) => priceWarningCell(
                  internalPriceBelowPurchasePrice(params.row as EntryRow, periods, allMeasures),
                  m, displayFormatter(m)(params.value)),
              }
            : m.valueType === 'QUANTITY'
              ? { renderCell: quantityAggregateCell(m) }
              : {}),
          // Editable (top-down) Total cells stay WHITE like the month input cells — white means
          // "you can type here"; only read-only value cells carry the tinted wash.
          cellClassName: ((totalEditable ? '' : 'nexo-readonly-cell') + first + last).trim(),
          headerClassName: `nexo-total-cell${first}${last}`,
        });
      });
    }
    for (const p of periods) {
      for (const m of periodMeasures) {
        cols.push({
          field: `${p.id}__${m.code}`,
          headerName: m.label,
          type: 'number',
          editable: m.kind === 'INPUT', // DERIVED columns (e.g. Värde) are read-only
          width: m.valueType === 'AMOUNT' ? 110 : 90, // money columns truncate at 90px
          ...measureCellProps(m),
          ...adHocAggregation(m, `${p.id}__${m.code}`),
          // Read-only DERIVED month cells (Värde) get the same tinted wash as the read-only
          // Totals, so white consistently means "you can type here".
          ...(m.kind === 'INPUT' ? {} : { cellClassName: 'nexo-readonly-cell' }),
          // The OQ-1 warning sits on exactly the offending month Pris cell(s); quantity months
          // get the per-unit tooltip cell on their aggregate rows (leaf cells render as before).
          ...(m.storageBinding === 'PLANLINE_UNITPRICE'
            ? {
                renderCell: (params: GridRenderCellParams) => {
                  const internal = rowInternalPrice(params.row as EntryRow, allMeasures);
                  const value = Number(params.value);
                  const offends = internal !== null && params.value != null && params.value !== ''
                    && !Number.isNaN(value) && value > internal;
                  return priceWarningCell(
                    offends ? { internalPrice: internal!, purchasePrice: value } : null,
                    m, displayFormatter(m)(params.value));
                },
              }
            : m.valueType === 'QUANTITY'
              ? { renderCell: quantityAggregateCell(m) }
              : {}),
          // Locale-aware editing: accept the user's decimal separator regardless of browser locale.
          renderEditCell: (params) => <MeasureEditCell {...params} />,
        });
      }
    }
    // pinnable/groupable:false on every column: pinning is programmatic (pinnedLeftFields) and
    // grouping is the view's row-dimension structure (rowGroupingModel) — the column menu must
    // not offer Pin/Unpin or Group by, or a user could break the fixed layout. Aggregation is
    // opt-in per column via adHocAggregation above; everything else defaults to non-aggregable.
    // (aggregable only gates the MENU; the programmatic aggregationModel still applies —
    // verified in MUI's getAggregationRules.)
    return cols.map(c => ({ ...c, pinnable: false, groupable: false, aggregable: c.aggregable ?? false }));
    // formatLocale: rebuild columns so number cells re-format when the user switches format locale.
  }, [outerCount, rowAxes, periods, periodMeasures, internalPriceMeasures, mappingReasonOptions, showTotals, topDownMeasures, viewIsUnitUniform, viewUnitCode, t, formatLocale]);

  // Group the measure columns under their month; a leading "Totalt" group holds the per-measure totals.
  const columnGroups = useMemo<GridColumnGroupingModel>(
    () => {
      const monthGroups = periods.map(p => ({
        groupId: p.id,
        headerName: p.name,
        // The group holding the focused cell is marked in the secondary hue (see sx below).
        ...(p.id === activeGroupId ? { headerClassName: 'nexo-active-group' } : {}),
        children: periodMeasures.map(m => ({ field: `${p.id}__${m.code}` })),
      }));
      if (!showTotals) return monthGroups;
      const totalGroup = {
        groupId: TOTAL_FIELD_PREFIX,
        headerName: t('Finance.PlanEntry.Total', { defaultValue: 'Totalt' }),
        headerClassName: activeGroupId === TOTAL_FIELD_PREFIX
          ? 'nexo-total-cell nexo-total-first nexo-total-last nexo-active-group'
          : 'nexo-total-cell nexo-total-first nexo-total-last',
        children: periodMeasures.map(m => ({ field: `${TOTAL_FIELD_PREFIX}${m.code}` })),
      };
      return [totalGroup, ...monthGroups];
    },
    [periods, periodMeasures, showTotals, activeGroupId, t],
  );

  const processRowUpdate = (updated: GridRowModel, original: GridRowModel): GridRowModel => {
    // Thin wrapper over the pure applyRowEdit: spread/broadcast an edited Total, recompute DERIVED
    // measures and the Total cells. (See applyRowEdit for why edited Totals are compared against the
    // committed/original rows, not the working row.)
    const row = applyRowEdit(updated as EntryRow, original as EntryRow, {
      topDownMeasures,
      derivedMeasures,
      measures,
      periods,
      weights: resolveWeights(profileLines, periods),
      showTotals,
    });
    setRows(prev => prev.map(r => (r.id === row.id ? row : r)));
    // Record which (row, period) cells this edit actually changed — a direct edit dirties one
    // period, a top-down Total spread dirties many — so Save sends only those.
    const changed = changedPeriodIds(row, original as EntryRow, periods, inputMeasures);
    if (changed.length > 0) {
      setDirtyCells(prev => {
        const next = new Set(prev);
        for (const pid of changed) next.add(cellKey(row.id, pid));
        return next;
      });
    }
    setDirty(true);
    return row;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Only the cells the user touched since load (dirty set). Each carries its combination's
      // free-dimension value ids (e.g. Affärstyp) + the one product group (if a hierarchy axis);
      // every value knows its own axis. A cleared cell is flagged for deletion server-side.
      const cells = composeSaveCells(rows, periods, inputMeasures, dirtyCells);
      if (cells.length === 0) return [] as string[]; // nothing changed — skip the round-trip
      const response = await apiClient.post('/api/finance/planline/save-cells', {
        planHeaderId: headerId,
        cells,
      });
      return response.data as string[];
    },
    onSuccess: () => {
      setApiValidationErrors([]);
      setDirty(false);
      setDirtyCells(new Set());
      // The save pipeline derives state the grid cannot compute locally — re-resolved internal
      // prices (the list may have changed since load), stamped units, PENDING flags. Refetch the
      // saved-cell snapshot; the rebuild effect (no longer dirty) folds it into the rows, so the
      // user sees the prices the server actually applied without reloading the page.
      void queryClient.invalidateQueries({ queryKey: ['planEntryCells', headerId] });
      // The save also lazily MATERIALISES DimensionValues the first time a combination is planned,
      // and the read-back joins each saved cell to its row via valueIdToKey — built from the
      // dimension-value list. Refetched cells carrying a value id the cached list has never seen
      // fail that join and silently drop, so the just-saved figures render as 0 until a remount
      // (issue #337). Refetch the list alongside the cells so the join learns the new ids.
      void queryClient.invalidateQueries({ queryKey: ['dimensionvalues'] });
    },
    onError: (error: unknown) => {
      const errors = getValidationErrors(error);
      setApiValidationErrors(errors.length > 0
        ? errors
        : [{ field: 'form', message: t('common.errorOccurred', { defaultValue: 'An error occurred' }), instancePath: '#', members: [] }]);
    },
  });

  const isSaving = saveMutation.isPending;
  const handleBack = () => navigate('/finance/plan-entry');
  const handleCancel = () => {
    setRows(buildRows());
    setDirty(false);
    setDirtyCells(new Set());
    setApiValidationErrors([]);
  };
  // Refresh (toolbar icon): discard any unsaved edits back to the last server snapshot, then refetch
  // EVERYTHING the grid is built from — the saved figures and header, but also every row-axis
  // source (dimension config, entity master lists, server-resolved rows, periods, the view). The
  // load-error banner says "reload to retry", so this button must actually retry a failed source —
  // a successful refetch clears its error and the banner retires itself. The ViewToolbar confirms
  // first when there are unsaved changes.
  const handleRefresh = () => {
    setRows(buildRows());
    setDirty(false);
    setDirtyCells(new Set());
    setApiValidationErrors([]);
    setDismissedLoadKey(null);
    for (const key of [
      ['planEntryCells', headerId],
      ['planheaders'],
      ['planHeaderFiscalYear', headerId],
      ['planviews'],
      ['fiscalperiods'],
      ['dimensiondefinitions'],
      ['dimensionvalues'],
      ['customers'],
      ['tradetypes'],
      ['suppliers'],
      ['productGroupSubtree'],
      ['resolveRowValues'],
      ['resolveAccountRows', headerId],
    ]) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  };

  if (headerLoading || viewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!header) {
    return <Box sx={{ p: 3 }}><Typography>{t('common.notFound', { defaultValue: 'Not found' })}</Typography></Box>;
  }

  return (
    // height:100% (not 100vh) so the page fits the Layout content area below the app header — matches the
    // standard grid pages (e.g. Sales/Orders); pb:2 on the content area then shows as a bottom margin.
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ValidationErrorBanner
        errors={[...loadBannerErrors, ...apiValidationErrors]}
        onExpand={() => setPanelOpen(true)}
        onClear={() => {
          setDismissedLoadKey(loadErrorKey);
          setApiValidationErrors([]);
          setPanelOpen(false);
        }}
      />
      <ViewToolbar onBack={handleBack} onRefresh={handleRefresh} hasUnsavedChanges={dirty}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={isSaving || !dirty}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            ...(!dirty || isSaving
              ? { borderColor: 'grey.300', color: 'grey.400' }
              : { borderColor: 'grey.400', color: 'text.secondary' }),
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="outlined"
          onClick={() => saveMutation.mutate()}
          disabled={isSaving || !dirty}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            ...(!dirty || isSaving
              ? { borderColor: 'grey.300', color: 'grey.400' }
              : { borderColor: 'primary.main', color: 'primary.main' }),
          }}
        >
          {isSaving ? t('common.saving') : t('common.save')}
        </Button>
        {/* Same Actions menu as the Plan Header detail view, so the budgeter can run header workflow
            actions (e.g. "Submit Budget") without leaving the entry grid. On success we refetch the
            header so the status in the title updates; errors surface in the shared validation panel. */}
        <PlanHeaderActionsMenu
          entityId={headerId}
          onCommandSuccess={() => {
            setApiValidationErrors([]);
            setPanelOpen(false);
            queryClient.invalidateQueries({ queryKey: ['planheaders'] });
          }}
          onCommandError={(error: unknown) => {
            const errors = getValidationErrors(error);
            setApiValidationErrors(errors.length > 0
              ? errors
              : [{ field: 'form', message: t('common.errorOccurred', { defaultValue: 'An error occurred' }), instancePath: '#', members: [] }]);
            setPanelOpen(true);
          }}
        />
      </ViewToolbar>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pb: 2, bgcolor: 'background.default' }}>
          {/* Title with the submission status as a soft secondary-tinted dot-chip right next to it.
              mt/mb match the generated detail views (title mt:1.5, mb:1) so toolbar→title→panel
              spacing is identical. */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, mb: 1, gap: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>{header.name}</Typography>
            {statusLabel && (
              <Chip
                size="small"
                icon={<CircleIcon />}
                label={statusLabel}
                sx={{
                  fontWeight: 600,
                  bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                  color: 'secondary.dark',
                  '& .MuiChip-icon': { color: 'secondary.main', fontSize: 8 },
                }}
              />
            )}
            {!canEdit && (
              <Chip
                size="small"
                label={t('Finance.PlanEntry.ReadOnly', { defaultValue: 'Read-only' })}
                sx={{
                  fontWeight: 600,
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.15),
                  color: 'text.secondary',
                }}
              />
            )}
          </Box>

          {/* Read-only header-context strip: which version/org-unit this entry belongs to and who
              it is assigned to — label-over-value facts with vertical dividers (not disabled
              input-look fields; these are never editable here). flexShrink:0 keeps it pinned above
              the grid, which scrolls independently below. */}
          <Paper elevation={1} sx={{ p: 1.5, mb: 1.5, flexShrink: 0 }}>
            {/* Content-width blocks (not equal thirds) so the facts sit close together. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 4, rowGap: 1, alignItems: 'flex-start' }}>
              {[
                {
                  label: t('DisplayName:PlanHeader.planVersionId', { defaultValue: 'Plan Version' }),
                  value: header.planVersionLabel ?? '',
                },
                {
                  label: t('DisplayName:PlanHeader.planViewId', { defaultValue: 'Plan View' }),
                  value: planView?.name ?? '',
                },
                {
                  label: t('DisplayName:PlanHeader.ownerOrgUnitId', { defaultValue: 'Planning Org Unit' }),
                  value: ownerOrgUnit?.label ?? '',
                },
                {
                  label: t('DisplayName:PlanHeader.assignedToPrincipalId', { defaultValue: 'Assigned To' }),
                  value: assignedToLabel,
                  emptyText: t('Finance.PlanEntry.NotAssigned', { defaultValue: 'Not assigned' }),
                },
              ].map((fact, i) => (
                <Box
                  key={fact.label}
                  sx={{
                    // Thin divider between the facts (none on the first); long values cap at 28rem
                    // and truncate instead of pushing the strip apart.
                    borderLeft: i === 0 ? 0 : 1,
                    borderColor: 'divider',
                    pl: i === 0 ? 0 : 4,
                    minWidth: 0,
                    maxWidth: '28rem',
                  }}
                >
                  <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.8 }}>
                    {fact.label}
                  </Typography>
                  {fact.value ? (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap title={fact.value}>
                      {fact.value}
                    </Typography>
                  ) : (
                    <Typography variant="subtitle1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      {fact.emptyText ?? '—'}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Grid controls row: the months toggle on the left, the distribution selector (top-down
              entry only) on the right. Hiding the months leaves Internpris + Totalt as a compact
              overview; the choice persists with the rest of the grid layout. */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1, flexShrink: 0 }}>
            <Button size="small" variant="outlined" onClick={toggleMonths}>
              {monthsHidden
                ? t('Finance.PlanEntry.ShowMonths', { defaultValue: 'Show months' })
                : t('Finance.PlanEntry.HideMonths', { defaultValue: 'Hide months' })}
            </Button>
            {allowTotalEntry && (
              <TextField
                select
                size="small"
                label={t('Finance.PlanEntry.DistributionProfile', { defaultValue: 'Distribution' })}
                value={selectedProfileId}
                onChange={e => setSelectedProfileId(e.target.value)}
                sx={{ minWidth: 200 }}
                // Hint moved off the field (it inflated the row height); kept as a tooltip.
                title={t('Finance.PlanEntry.DistributionHint', { defaultValue: 'Spread typed totals across months' })}
              >
                <MenuItem value="">
                  {t('Finance.PlanEntry.EvenDistribution', { defaultValue: 'Even' })}
                </MenuItem>
                {(profiles ?? []).filter(p => p.isActive).map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          {configWarnings.map((msg, i) => (
            <Alert key={i} severity="warning" sx={{ mb: 1 }}>{msg}</Alert>
          ))}

          <DataGridPremium
            density="compact"
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            columnGroupingModel={columnGroups}
            // The outer row dimensions (all but the innermost) nest as collapsible groups, in
            // SortOrder order; the innermost row dimension is the leaf data row. A single row
            // dimension → no grouping (flat grid).
            rowGroupingModel={groupingFields}
            // Cross-row aggregation is menu-opt-in only (no defaults — user decision 2026-08-26):
            // the model starts empty and the change handler makes column-menu picks stick. On a
            // mixed-unit view a quantity column's only offered sum is the per-unit one (spec
            // §12.3 — asterisk + tooltip, never a sum across units).
            aggregationFunctions={aggregationFunctions}
            aggregationModel={aggregationModel}
            onAggregationModelChange={setAggregationModel}
            defaultGroupingExpansionDepth={-1}
            groupingColDef={{
              headerName: t('Finance.PlanEntry.Group', { defaultValue: 'Group' }),
              width: 220,
              pinnable: false,
            }}
            // Column order and pinning are owned by the Plan View definition (measure sortOrder /
            // pinnedLeftFields): no drag-reorder, no pivoting, and no Pin menu items (pinnable:false
            // per column). Sorting stays available to the user.
            disableColumnReorder
            disablePivoting
            initialState={{ pinnedColumns: { left: pinnedLeftFields } }}
            columnVisibilityModel={visibilityModel}
            onColumnVisibilityModelChange={setVisibilityModel}
            // Read-only mode for oversight viewers (subtree/ViewAll) and submitted headers: the
            // per-column `editable` flags still shape keyboard lanes, but no cell enters edit mode.
            isCellEditable={() => canEdit}
            cellSelection
            // A spreadsheet treats the selection as the paste ANCHOR, not a clip boundary: stand in
            // one cell, paste a 3×2 block, and 3×2 cells are filled. MUI pastes INTO the selection
            // instead — `defaultPasteResolver` takes the selection branch the moment more than one
            // cell is selected, and silently drops every clipboard value that does not fit — so you
            // had to select exactly as many cells as you had copied. Collapsing a range to its
            // top-left cell hands the paste to MUI's own anchor branch, which fills down and right
            // from there. A SINGLE pasted value keeps the selection: filling every selected cell
            // with one value is a spreadsheet's behaviour too, and that is the branch that does it.
            onBeforeClipboardPasteStart={async ({ data }) => {
              const api = apiRef.current;
              if (!api) return;
              const isSingleValue = data.length === 1 && data[0].length === 1;
              const selected = api.getSelectedCellsAsArray();
              if (isSingleValue || selected.length <= 1) return;
              // Visual order (row by row, left to right) — the same order the resolver itself
              // relies on when it walks the selection, so [0] is the top-left cell.
              const anchor = selected[0];
              api.setCellSelectionModel({});
              api.setCellFocus(anchor.id, anchor.field);
            }}
            // Cell selection is the selection model here — clicking a cell must NOT also select its
            // ROW. MUI's paste resolver checks row selection FIRST: with any row selected, a
            // multi-value clipboard block is pasted into the selected rows starting at the first
            // visible column (the row label, which is read-only), so every write is rejected and the
            // paste silently does nothing. Single values dodge that branch, which is why pasting one
            // cell worked while pasting a column did not.
            disableRowSelectionOnClick
            showCellVerticalBorder
            showColumnVerticalBorder
            hideFooter
            getRowClassName={(params) => {
              // Auto-generated grouping rows (the section bands, e.g. "Egen avverkning") get a
              // tinted band; MUI prefixes their id with `auto-generated-row-`. The band lightens
              // per nesting level (outermost darkest) so the hierarchy reads at a glance.
              if (!String(params.id).startsWith('auto-generated-row-')) return '';
              const depth = apiRef.current?.getRowNode(params.id)?.depth ?? 0;
              return `nexo-group-row nexo-group-row-d${Math.min(depth, 2)}`;
            }}
            processRowUpdate={processRowUpdate}
            onCellKeyDown={(params, event) => {
              const key = event.key;
              const isArrow = key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight';
              if (key !== 'Tab' && !isArrow) return;
              // Shift+Arrow = Excel-style range selection. MUI's `cellSelection` already implements it
              // on this very event (useGridCellSelection → selectCellRange), so hand it straight back.
              // We must NOT fall through: `moveTo` sets event.defaultMuiPrevented, and useGridEvent
              // gates EVERY internal cellKeyDown subscriber on that flag — so swallowing Shift+Arrow
              // here silently disabled range selection for the whole grid. (Shift+Tab is ours: it is
              // backward navigation, not selection.)
              if (isArrow && event.shiftKey) return;
              const api = apiRef.current;
              if (!api) return;
              const rIdx = rows.findIndex(r => r.id === params.id);
              if (rIdx === -1) return;

              // Move into an editable cell, committing the current edit. Both Tab and the arrows only
              // ever target editable columns, so read-only DERIVED "Värde"/Total cells are skipped
              // entirely — you can't land in one and get stuck.
              //
              // The target is left in VIEW mode on purpose. MUI's clipboard paste bails out when the
              // focused cell is in edit mode ("Do not paste data when the cell is in edit mode" —
              // useGridClipboardImport), so eagerly re-entering edit mode here disabled Ctrl+V across
              // the whole grid. Typing a character still starts edit mode and overwrites the value
              // (MUI's own canStartEditing), so the spreadsheet feel is unchanged.
              const moveTo = (nr: number, targetField: string) => {
                if (nr < 0 || nr >= rows.length || !targetField) return;
                event.defaultMuiPrevented = true;
                event.preventDefault();
                const targetId = rows[nr].id;
                // ONLY when the source cell is actually editing. `stopCellEditMode` throws
                // ("MUI X: The cell ... is not in edit mode") on a view-mode cell, and since
                // defaultMuiPrevented/preventDefault are already set above, that exception would kill
                // the move AND MUI's own fallback navigation — arrows and Tab would do nothing at all.
                if (api.getCellMode(params.id, params.field) === 'edit') {
                  api.stopCellEditMode({ id: params.id, field: params.field });
                }
                api.setCellFocus(targetId, targetField);
              };

              // Navigate only the editable columns, skipping read-only DERIVED "Värde" cells. The
              // editable Total cells (top-down entry) form their OWN lane: Tab/arrows from a Total
              // cell stay among the Total cells (next/prev row), never stepping into a month cell.
              // This matters because typing a Total spreads it into the months on commit; if Tab
              // immediately opened a freshly-spread month cell, its editor would mount with the
              // pre-spread (empty) value and wipe the spread on the next move.
              const allEditable = columns.filter(c => c.editable).map(c => c.field);
              const onTotal = params.field.startsWith(TOTAL_FIELD_PREFIX);
              const editableCols = allEditable.filter(f => f.startsWith(TOTAL_FIELD_PREFIX) === onTotal);
              const eIdx = editableCols.indexOf(params.field);
              if (eIdx === -1) return; // not on an editable cell (e.g. clicked a read-only cell)

              // Arrows always move cell-to-cell (never step through the digits of the value), so
              // navigating with the keyboard stays fast. The value is selected on entry, so typing
              // overwrites it; positioning the caret inside a number is done by double-click/click.
              if (key === 'Tab') {
                let ne = eIdx + (event.shiftKey ? -1 : 1);
                let nr = rIdx;
                if (ne >= editableCols.length) { ne = 0; nr = rIdx + 1; }
                else if (ne < 0) { ne = editableCols.length - 1; nr = rIdx - 1; }
                moveTo(nr, editableCols[ne]);
              } else if (key === 'ArrowUp') {
                moveTo(rIdx - 1, params.field);
              } else if (key === 'ArrowDown') {
                moveTo(rIdx + 1, params.field);
              } else if (key === 'ArrowLeft') {
                let ne = eIdx - 1, nr = rIdx;
                if (ne < 0) { ne = editableCols.length - 1; nr = rIdx - 1; }
                moveTo(nr, editableCols[ne]);
              } else if (key === 'ArrowRight') {
                let ne = eIdx + 1, nr = rIdx;
                if (ne >= editableCols.length) { ne = 0; nr = rIdx + 1; }
                moveTo(nr, editableCols[ne]);
              }
            }}
            sx={(theme) => {
              // Base hue = the topbar/accent colour. The grid's washes, borders, hover and selection all
              // track the user's chosen accent (red/green/blue/…) so they stay in sync with the rest of
              // the app. The ONE exception is the section group band, which is a fixed blue-grey (#eef3f8)
              // by design.
              const base = theme.palette.primary.main;
              // An OPAQUE accent tint: paint the translucent hue over a solid paper background so the
              // pinned/total cells don't let scrolling month columns bleed through underneath them.
              const solidTint = (a: number) => ({
                backgroundColor: theme.palette.background.paper,
                backgroundImage: `linear-gradient(${alpha(base, a)}, ${alpha(base, a)})`,
              });
              return {
                // Fill the remaining page height (flex child of the content column); minHeight:0 lets the
                // grid shrink so its own body scrolls instead of overflowing the page.
                flex: 1,
                minHeight: 0,
                // Soft rounded card with a hairline tinted border; drop the default hard outline.
                border: `1px solid ${alpha(base, 0.12)}`,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                // Lighten every internal divider to a faint tint of the accent hue.
                '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaders, & .MuiDataGrid-filler, & .MuiDataGrid-row': {
                  borderColor: alpha(base, 0.08),
                },
                '--DataGrid-rowBorderColor': alpha(base, 0.08),
                // Column headers: a faint accent wash, the small grouped month/measure labels in a muted
                // uppercase treatment so the data below reads as the focus.
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: `1px solid ${alpha(base, 0.14)}`,
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: alpha(base, 0.04),
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: alpha(theme.palette.text.primary, 0.6),
                },
                '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeader--filledGroup': {
                  color: alpha(theme.palette.text.primary, 0.75),
                },
                // Section grouping rows (e.g. "Egen avverkning"): an accent-tinted band + medium weight,
                // opaque (over paper) so it doesn't let scrolling month columns bleed through. The extra
                // .MuiDataGrid-row on the selector lifts specificity above the pinned-cell rule below, so
                // the band carries unbroken across the frozen Totalt section. One shade per nesting
                // depth — outermost darkest, lighter for each level — so the hierarchy reads at a glance.
                '& .nexo-group-row, & .nexo-group-row.MuiDataGrid-row .MuiDataGrid-cell': {
                  fontWeight: 600,
                },
                '& .nexo-group-row-d0, & .nexo-group-row-d0.MuiDataGrid-row .MuiDataGrid-cell': solidTint(0.10),
                '& .nexo-group-row-d1, & .nexo-group-row-d1.MuiDataGrid-row .MuiDataGrid-cell': solidTint(0.055),
                '& .nexo-group-row-d2, & .nexo-group-row-d2.MuiDataGrid-row .MuiDataGrid-cell': solidTint(0.03),
                '& .nexo-group-row-d0:hover, & .nexo-group-row-d0.MuiDataGrid-row:hover .MuiDataGrid-cell': solidTint(0.13),
                '& .nexo-group-row-d1:hover, & .nexo-group-row-d1.MuiDataGrid-row:hover .MuiDataGrid-cell': solidTint(0.085),
                '& .nexo-group-row-d2:hover, & .nexo-group-row-d2.MuiDataGrid-row:hover .MuiDataGrid-cell': solidTint(0.055),
                // Gentle row hover for the data rows.
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: alpha(base, 0.035),
                },
                // Pinned left DATA cells: opaque paper so the scrolling month columns don't bleed through.
                // (Declared before the total rules so the stronger-specificity total tint wins on those cells.)
                '& .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft': {
                  backgroundColor: theme.palette.background.paper,
                },
                // Pinned left HEADER cells (GROUP / SORTIMENT): the same accent header wash as the rest of
                // the header row, opaque so scrolling month headers don't bleed through.
                '& .MuiDataGrid-columnHeader--pinnedLeft': {
                  ...solidTint(0.04),
                },
                // Read-only VALUE cells (derived Värde per month, non-editable Totals, Internpris)
                // read as results through bold values + a faint accent wash. Editable cells — the
                // months AND the top-down Total cells — stay white, so white consistently means
                // "you can type here".
                '& .nexo-readonly-cell': { fontWeight: 700, ...solidTint(0.03) },
                // The Totalt section reads as its own block: a slightly stronger header wash than
                // the month headers (0.09 vs 0.04) and a crisp LEFT frame edge on its first column
                // (cells + headers + the group header), mirroring the existing pinned-edge divider
                // on its right. Cells stay untinted — white must keep meaning "you can type here".
                '& .nexo-total-cell.MuiDataGrid-columnHeader': { ...solidTint(0.09) },
                '& .nexo-total-cell .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                // Compound selectors (2 classes) so the frame edges beat MUI's own faint
                // cell/header border rules regardless of style-injection order.
                '& .MuiDataGrid-cell.nexo-total-first, & .MuiDataGrid-columnHeader.nexo-total-first': {
                  borderLeft: `2px solid ${alpha(base, 0.22)}`,
                },
                '& .MuiDataGrid-cell.nexo-total-last, & .MuiDataGrid-columnHeader.nexo-total-last': {
                  borderRight: `2px solid ${alpha(base, 0.22)}`,
                },
                // The column group (month or Totalt) holding the focused cell: tinted header in the
                // theme's secondary hue with an accent top bar (inset shadow — no layout shift),
                // matching the status chip. OPAQUE via the solidTint technique (paper base + tinted
                // gradient) — a plain alpha background lets the scrolling month headers bleed
                // through under the PINNED Totalt header when it is focused. The gradient also
                // replaces the Totalt group's own primary-tinted one (nexo-total-cell), so the two
                // hues never stack.
                '& .MuiDataGrid-columnHeader.nexo-active-group': {
                  backgroundColor: theme.palette.background.paper,
                  backgroundImage: `linear-gradient(${alpha(theme.palette.secondary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.12)})`,
                  boxShadow: `inset 0 3px 0 0 ${theme.palette.secondary.main}`,
                },
                '& .nexo-active-group .MuiDataGrid-columnHeaderTitle': {
                  color: theme.palette.secondary.dark,
                  fontWeight: 700,
                },
                // (The old pinned-section divider rule targeted MuiDataGrid--pinnedColumns-left,
                // a wrapper MUI v8 removed — the frame edges above carry that divider now.)
                // Selected cell highlight in the accent hue rather than the default grey.
                '& .MuiDataGrid-cell.Mui-selected, & .MuiDataGrid-cell--editing': {
                  backgroundColor: alpha(base, 0.08),
                },
                // Hide the native number-input spin buttons in editable cells.
                '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '& input[type=number]': { MozAppearance: 'textfield' },
              };
            }}
          />
          {/* AFTER the grid on purpose — see GridLayoutPersistence. Keyed per plan view: views
              have different measure/period columns, so their layouts must not share a slot. */}
          {planView?.id && <GridLayoutPersistence apiRef={apiRef} variant={planView.id} />}
        </Box>

        {/* The validation panel docks to the RIGHT of the content, as in the detail/list views —
            its collapsed 24px toggle strip only lays out correctly as a row flex sibling. */}
        <ValidationErrorPanel
          errors={[...loadBannerErrors, ...apiValidationErrors]}
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
        />
      </Box>
    </Box>
  );
}
